import React, { useRef, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/utils';

interface TabSliderProps {
  children: React.ReactNode;
  className?: string;
}

export function TabSlider({ children, className }: TabSliderProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(Math.ceil(scrollLeft + clientWidth) < scrollWidth);
    }
  };

  useEffect(() => {
    checkScroll();
    // Use ResizeObserver to detect when children change size
    const ro = new ResizeObserver(checkScroll);
    if (scrollRef.current) ro.observe(scrollRef.current);
    window.addEventListener('resize', checkScroll);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', checkScroll);
    };
  }, [children]);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = 250;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
      setTimeout(checkScroll, 300);
    }
  };

  const isOverflowing = canScrollLeft || canScrollRight;

  return (
    <div
      className={cn(
        'relative flex items-center w-full max-w-full',
        isOverflowing ? 'px-8' : '',
        className
      )}
    >
      {canScrollLeft && (
        <button
          onClick={() => scroll('left')}
          className="absolute left-0 z-10 p-1.5 bg-white border border-gray-200 rounded-full shadow-md text-[#082261] hover:bg-gray-50 transition-all flex items-center justify-center"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
      )}

      <div
        ref={scrollRef}
        onScroll={checkScroll}
        className={cn(
          'flex overflow-x-auto scrollbar-hide py-2 px-1 items-center w-full',
          isOverflowing ? '' : 'justify-center'
        )}
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        <div className="flex flex-nowrap items-center min-w-max gap-2 sm:gap-4">{children}</div>
      </div>

      {canScrollRight && (
        <button
          onClick={() => scroll('right')}
          className="absolute right-0 z-10 p-1.5 bg-white border border-gray-200 rounded-full shadow-md text-[#082261] hover:bg-gray-50 transition-all flex items-center justify-center"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      )}

      {/* Hide scrollbar injected via style */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
}
